import axios from 'axios'
import store from '@/lti/store'
import qs from 'qs'
import { defaultErrorMessage } from '@/helpers'

const token = sessionStorage.getItem('jwt')

const api = axios.create({
  baseURL: '/lti/1.3/api/',
  headers: { Authorization: 'Bearer ' + token },
})

let refreshTokenPromise = Promise.resolve()

api.interceptors.request.use(config => {
  // if we get a refreshToken request, just pass along
  if (config._isRefreshToken) {
    return Promise.resolve(config)
  }

  config.paramsSerializer = params => {
    return qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: true,
    })
  }

  // every other request is "queued" in the promise, to make sure the request is executed after the token refresh
  // is handled. If we aren't refreshing the token, the request will execute immediately since the Promise is
  // resolved. Since the token can be changed if the Promise needs to wait, we re-set the authorization header to
  // the default value
  return refreshTokenPromise.then(() => {
    config.headers.Authorization = api.defaults.headers.Authorization
    return Promise.resolve(config)
  })
})

api.interceptors.response.use(
  r => r,
  error => {
    const originalRequest = error.config

    if (originalRequest._isRefreshToken) {
      // something went wrong with refreshing the token. Show the user a message.
      return store.dispatch('setJwtTokenValidity', false).then(() => {
        return Promise.reject(error)
      })
    }

    if (error.response && error.response.status === 401) {
      // this Promise is chained to ensure this will be executed/resolved first before handling new requests/responses
      refreshTokenPromise = refreshTokenPromise.then(() => {
        // if the rejected request has the same bearer token as the one we cached in the client, we ty to refresh the token
        if (originalRequest.headers.Authorization === api.defaults.headers.Authorization) {
          return refreshToken().then(response => {
            if (response.status === 201) {
              const newToken = response.data.token

              // set the new token in sessionStorage and change the default authorization header
              sessionStorage.setItem('jwt', newToken)
              api.defaults.headers.Authorization = 'Bearer ' + newToken
            }
          }).catch(error => defaultErrorMessage(error))
        }
      })

      // retry the failed original request, and replace the authorization token that should be refreshed at this point
      return refreshTokenPromise.then(() => {
        originalRequest.headers.Authorization = api.defaults.headers.Authorization
        return axios({ ...originalRequest, baseURL: '' })
      })
    }

    return Promise.reject(error)
  },
)

export { api }

export const getUser = () => api.get('user')

// course with events
export const getCourse = (params) => api.get('courses', { params })
export const getPrograms = (params) => api.get('courses/programs', { params })
export const guessCourse = () => api.get('courses/guess')
export const suggestCourse = (postData) => api.post('courses/suggest', postData)
export const chooseCourse = (courseId) => api.post('courses/choose', { courseId })
export const disableConfiguration = () => api.post('courses/disable-configuration')

// events
export const getCourseEvents = (id) => api.get(`courses/${id}/events`)
export const getLesson = (courseId, lessonId, params) => api.get(`courses/${courseId}/lessons/${lessonId}`, { params })
export const getQuestionTypes = () => api.get('questions/types')
export const getLessonQuestions = (courseId, lessonId) => api.get(`courses/${courseId}/lessons/${lessonId}/questions`)
export const storeQuestion = (courseId, lessonId, params) => api.post(`courses/${courseId}/lessons/${lessonId}/questions`, params)
export const deleteQuestion = (courseId, lessonId, questionId) => api.delete(`courses/${courseId}/lessons/${lessonId}/questions/${questionId}`)
export const suggestQuestion = (params) => api.post('questions/suggest', params)

// groups seen by lecturer
export const getGroups = (courseId) => api.get(`courses/${courseId}/groups`)
export const getOwnGroups = (courseId) => api.get(`courses/${courseId}/groups/own`)
export const updateOwnGroups = (courseId, groupIds) => api.post(`courses/${courseId}/groups/own/edit`, { groupIds })

// course student attendance
export const getCourseStudentAttendance = (courseId) => api.get(`courses/${courseId}/student-attendances`)

// attendance overview
export const getCourseMatrixLessons = () => api.get('courses/lessons/matrix/lesson-api')
export const getCourseMatrixStudents = (params) => api.get('courses/lessons/matrix/student-api', { params })
export const getCourseMatrixLessonDetails = (params) => api.get('courses/lessons/matrix/api', { params })
export const getCourseMatrixLessonsExport = (params) => api.get('courses/lessons/matrix/export', { params })

// attendance goal
export const getAttendanceGoals = (courseId) => api.get(`courses/${courseId}/goals`)

// students of event for student table
export const getAttendances = (lessonId) => api.get(`lessons/${lessonId}/attendances`)

// students of event for student table
export const storeAttendancesByNumber = (courseId, lessonId, postData) => api.post(`courses/${courseId}/lessons/${lessonId}/attendances`, postData)

// registration list api
export const getContextLabels = (courseId, lessonId) => api.get(`courses/${courseId}/lessons/${lessonId}/registration/context-labels`)
export const getMinutesMissingConfig = (courseId, lessonId) => api.get(`courses/${courseId}/lessons/${lessonId}/registration/minutes-missing-config`)
export const getStudents = (courseId, lessonId) => api.get(`courses/${courseId}/lessons/${lessonId}/registration/students`)
export const getAttendedStudentIds = (courseId, lessonId) => api.get(`courses/${courseId}/lessons/${lessonId}/registration/attended-student-ids`)
export const storeAttendances = (courseId, lessonId, postData) => api.post(`courses/${courseId}/lessons/${lessonId}/registration/attendances`, postData)
export const storeContext = (courseId, lessonId, postData) => api.post(`courses/${courseId}/lessons/${lessonId}/registration/contexts`, postData)
export const storeNewStudent = (courseId, lessonId, postData) => api.post(`courses/${courseId}/lessons/${lessonId}/registration/new-student`, postData)
export const suggestNewStudent = (courseId, lessonId, postData) => api.post(`courses/${courseId}/lessons/${lessonId}/registration/suggest-new-student`, postData)
export const closeKey = (courseId, lessonId) => api.put(`courses/${courseId}/lessons/${lessonId}/rps/close-key`)
export const confirmAttendances = (courseId, lessonId) => api.get(`courses/${courseId}/lessons/${lessonId}/attendances/verify`)
export const getLessonDetails = (courseId, lessonId) => api.get(`courses/${courseId}/lessons/${lessonId}/registration/lesson-details`)

export const getTimeKey = (courseId, lessonId, rpId) => api.get(`courses/${courseId}/lessons/${lessonId}/rps/${rpId}/time-key`)
export const getSingleKey = (courseId, lessonId, rpId) => api.get(`courses/${courseId}/lessons/${lessonId}/rps/${rpId}/single-key`)
export const closeDynamicKey = (courseId, lessonId, rpId) => api.put(`courses/${courseId}/lessons/${lessonId}/rps/${rpId}/dynamic-key/close`)

// api for students
export const getStudentCourse = () => api.get('student/courses')
export const getEvents = (params) => api.get('student/events', { params })
export const getTicketQrCode = (hash) => api.get(`student/tickets/${hash}/qr`, { responseType: 'blob' })

// Subscribable
export const getSubscribables = (params) => api.get('student/subscribables', { params })
export const getSubscribable = (subscribableId, queryParams) => api.get(`student/subscribables/${subscribableId}`, { params: queryParams })
export const getSubscribableGroupings = (params) => api.get('student/subscribable-groupings', { params })
export const storeSubscription = (subscribableId, data) => api.post(`student/subscribables/${subscribableId}/subscriptions`, data)
export const updateSubscribableAnswers = (subscribableId, data) => api.put(`student/subscribables/${subscribableId}/answers`, data)
export const deleteSubscription = (subscriptionId) => api.delete(`student/subscriptions/${subscriptionId}`)
export const updateSubscription = (subscriptionId, data) => api.put(`student/subscriptions/${subscriptionId}`, data)
export const subscribeToQueue = (subscribableId, data) => api.post(`student/subscribables/${subscribableId}/queue`, data)
export const unsubscribeFromQueue = (subscribableId) => api.delete(`student/subscribables/${subscribableId}/queue`)

export const getSubscribableGroupingDetails = (subscribableGrouping, queryParams) => api.get(`api/subscribable-grouping-details/${subscribableGrouping}`, { params: queryParams })
export const validateSubscribableGrouping = (subscribableGroupingId, queryParams) => api.post(`student/api/subscribable-groupings/${subscribableGroupingId}/validate`, { params: queryParams })
export const confirmSubscribableGrouping = (subscribableGroupingId, queryParams) => api.post(`student/api/subscribable-groupings/${subscribableGroupingId}/confirm`, { params: queryParams })
export const getSubscriptionHistory = (queryParams) => api.get('student/subscriptions-history', { params: queryParams })
export const getFilterOptions = (queryParams) => api.get('student/subscribable/filter-options', { params: queryParams })
export const getSubscribableStatistics = () => api.get('student/subscribable/statistics')
export const getSubscribableConfig = () => api.get('student/subscribable/config')
export const getSubscribableCount = (queryParams) => api.get('student/subscribable/count', { params: queryParams })

export const getMentors = (queryParams) => api.get('student/mentors', { params: queryParams })
export const createThread = (data) => api.post('student/advises', data)
export const addReaction = (adviseId, data) => api.post(`student/advises/${adviseId}/messages`, data)
export const completeAdvise = (adviseId) => api.post(`student/advises/${adviseId}/complete`)

export const postKey = (params) => api.post('student/keys', params)
export const postTimeKey = (params) => api.post('student/time-keys', params)
export const getQuestions = () => api.get('student/questions')
export const postQuestion = (params) => api.post('student/questions', params)
export const getPhoto = (photoUrl) => api.get(`students${photoUrl}`, { responseType: 'arraybuffer' })

export const getEventsLecturerFlow = (params) => api.get('lecturer-flow/events', params)
export const getEventLecturerFlow = (id) => api.get(`lecturer-flow/events/${id}`)
export const storeEventLecturerFlow = (id, postData) => api.post(`lecturer-flow/events/${id}`, postData)
export const getOwnLecturerFlow = (params) => api.get('lecturer-flow/lecturers/own', params)

export const getDownloadLinks = (params) => api.get('download-links', params)
export const downloadFile = (token) => api.get(`download-links/${token}`, { responseType: 'blob' })
// LTI refresh logic
export const refreshToken = (payload) => api.post('auth/token', payload, { _isRefreshToken: true, baseURL: '/lti/1.3/' })
